import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../app/store";
import { Logger } from "../../tools/Logger";
import { bindThunk, setUavNotifications } from "./aircraftSlice";
import { errorHasMessage, Sim, Uav } from "./types";
import { isLocalIp } from "../../constants";
import { getAircraftList } from "./aircraftAPI";
import { logToDd } from "../../tools/ddLog";

const TAG = "aircraftActions";
export const pingPromise = async (url: string, port: string = ":5001") => {
  try {
    await axios.get(`https://${url}${port}/healthinfo/time`, {
      timeout: 10000,
    });

    return { url };
  } catch (err) {
    throw err;
  }
};

export const pingThunk = createAsyncThunk<
  {},
  { sims: Sim[]; uavId: string; port: string },
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("aircraft/ping", async ({ sims, uavId, port }, thunkApi) => {
  try {
    Logger.info(TAG, `ping start`);
    const pings = await Promise.any(
      sims.map((sim) => pingPromise(sim.ip, port))
    );
    Logger.info(TAG, `ping succeeded`);
    return thunkApi.fulfillWithValue(
      { uavId },
      {
        message: `Ping promise succeeded ${pings?.url}`,
      }
    );
  } catch (err) {
    thunkApi.dispatch(
      setUavNotifications({
        uavId,
        notification: {
          msg: "Ping Failed.",
          type: "PING_FAILED",
          action: ["DISCONNECT"],
          level: 5,
          timestamp: Date.now(),
          info: true,
        },
      })
    );
    thunkApi.rejectWithValue(err, {
      message: `Ping failed ${uavId}`,
    });
    throw new Error(`Ping failed on both sims.`);
  }
});

export const connectThunk = createAsyncThunk<
  {},
  { sims: Sim[]; uavId: string; port: string },
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("aircraft/connect", async ({ sims, uavId, port }, thunkApi) => {
  try {
    const role = "p1";
    Logger.info(TAG, `connect start`);
    const localIp = sims.some((sim) => {
      return isLocalIp(sim.ip);
    });

    if (!localIp) {
      await thunkApi.dispatch(pingThunk({ sims, uavId, port })).unwrap();
    }
    Logger.info(TAG, `connect succeeded`);

    await thunkApi
      .dispatch(
        bindThunk({
          role: role,
          sims,
          uavId,
          port,
        })
      )
      .unwrap();
    logToDd(
      `connect succeeded ${uavId} ${sims.map((sim) => sim.ip).join(",")}`,
      "aircraftActions",
      {
        uavId,
        sims,
        port,
      },
      uavId
    );
    return thunkApi.fulfillWithValue(
      {},
      {
        message: "Bind!",
      }
    );
  } catch (err) {
    if (errorHasMessage(err)) {
      return thunkApi.rejectWithValue(err, {
        message: "Bind failed: " + err?.message,
      });
    }
    return thunkApi.rejectWithValue(err, {
      message: "Bind failed",
    });
  }
});
export const getAircraftListThunk = createAsyncThunk<
  Uav[],
  {},
  {
    fulfilledMeta: { message?: string };
    rejectedMeta: { message?: string };
    state: RootState;
  }
>("session/getAircraftList", async (_, thunkApi) => {
  try {
    const response = await getAircraftList();
    return thunkApi.fulfillWithValue(response, {});
  } catch (error) {
    console.error(error);

    if (errorHasMessage(error)) {
      return thunkApi.rejectWithValue(error, {
        message: "Get aircraft list failed: " + error?.message,
      });
    }
    return thunkApi.rejectWithValue(error, {
      message: "Get aircraft list failed",
    });
  }
});
